// firebaseService.js
import { db, storage } from '../firebase';
import {
    collection,
    addDoc,
    onSnapshot,
    query,
    orderBy,
    getDocs,
    getDoc,
    doc,
    where,
    deleteDoc,
    updateDoc,
    serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage, uploadBytesResumable } from 'firebase/storage';


export const uploadImage = (file, folder) => {
    return new Promise((resolve, reject) => {
        const storage = getStorage();
        const storageRef = ref(storage, `${folder}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                // 진행 상태를 보고 싶다면 추가 가능
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log('Upload is ' + progress + '% done');
            },
            (error) => {
                console.error('Upload error:', error);
                reject(error);
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve(downloadURL);
            }
        );
    });
};

// 방명록 메시지 추가 함수
export const addMessage = async (message, workspaceId) => {
    const workspaceRef = doc(db, 'workspaces', workspaceId);
    const workspaceDoc = await getDoc(workspaceRef);
    const workspaceData = workspaceDoc.data();
    const isApprovalRequired = workspaceData.isApprovalRequired;

    const messagesRef = collection(db, 'workspaces', workspaceId, isApprovalRequired ? 'pending_messages' : 'messages');

    const messageData = {
        message: {
            affiliation: message.affiliation || "",
            image: message.image || null,
            message: message.message || "",
            name: message.name || "",
            phone: message.phone || "",
            position: message.position || "",
            timestamp: message.timestamp || serverTimestamp()
        },
        timestamp: serverTimestamp()
    };

    if (isApprovalRequired) {
        messageData.status = 'pending';
    }

    const docRef = await addDoc(messagesRef, messageData);
    return docRef.id;
};

// 워크스페이스 목록 불러오기 함수
// 특정 워크스페이스의 데이터를 가져오는 함수
export const getWorkspaces = async () => {
    const workspaceCollection = collection(db, 'workspaces');
    // 최신순 정렬 쿼리 추가
    const q = query(workspaceCollection, orderBy('createdAt', 'desc')); // 최신순(desc) 정렬
    const workspaceSnapshot = await getDocs(q);

    console.log('Number of documents in workspace:', workspaceSnapshot.size);

    return workspaceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// 특정 워크스페이스에 데이터를 추가하는 함수
// Firestore에 새 워크스페이스 문서를 추가하는 함수
export const createWorkspace = async (workspaceData) => {
    try {
        const docRef = await addDoc(collection(db, 'workspaces'), {
            ...workspaceData,          // 워크스페이스 데이터 (예: 이름 등)
            createdAt: new Date(),
        });
        console.log('Workspace created with ID: ', docRef.id);
        return docRef.id;
    } catch (e) {
        console.error('Error creating workspace: ', e);
        throw e;
    }
};

// 워크스페이스 삭제 함수
export const deleteWorkspace = async (workspaceId) => {
    try {
        const workspaceDoc = doc(db, 'workspaces', workspaceId);
        await deleteDoc(workspaceDoc);
    } catch (error) {
        console.error('Error deleting workspace:', error);
        throw error;
    }
};

// 워크스페이스 업데이트 함수
export const updateWorkspace = async (workspaceId, data) => {
    const workspaceRef = doc(db, 'workspaces', workspaceId);
    await updateDoc(workspaceRef, data);
};


export const subscribeToMessages = async (workspaceId, callback) => {
    try {
        const messagesCollection = collection(db, `workspaces/${workspaceId}/messages`);
        const messagesQuery = query(messagesCollection, orderBy('timestamp', 'asc'));

        return onSnapshot(messagesQuery, (snapshot) => {
            const messages = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    timestamp: data.timestamp
                };
            });
            callback(messages);
        });
    } catch (error) {
        console.error('Error subscribing to messages:', error);
    }
};

/**
 * 특정 워크스페이스의 배경 이미지와 로고를 가져오는 함수
 * @param {string} workspaceId - 워크스페이스의 문서 ID
 * @returns {Promise<Object>} - 배경 이미지와 로고 데이터를 반환
 */
export const getWorkspaceSettings = async (workspaceId) => {
    try {
        // Firestore에서 해당 워크스페이스 문서를 참조
        const workspaceDocRef = doc(db, 'workspaces', workspaceId);
        const workspaceSnapshot = await getDoc(workspaceDocRef);

        // 문서가 존재하지 않을 경우 처리
        if (!workspaceSnapshot.exists()) {
            console.error(`Workspace with ID ${workspaceId} does not exist.`);
            return null;
        }

        // 문서 데이터 가져오기
        const workspaceData = workspaceSnapshot.data();

        // 배경 이미지와 로고만 반환
        const settings = {
            backgroundImage: workspaceData.backgroundImage || null,
            logo: workspaceData.logo || null, // 기존 호환성 유지
            leftLogo: workspaceData.leftLogo || workspaceData.logo || null,
            rightLogo: workspaceData.rightLogo || null,
            description: workspaceData.description || null,
            name: workspaceData.name || null,
            nameColor: workspaceData.nameColor || '#FFFFFF',
            isMessageBoardOpen: workspaceData.isMessageBoardOpen || null,
            guestbookBackgroundImage: workspaceData.guestbookBackgroundImage || null,
            privacyAgreement: workspaceData.privacyAgreement || null,
            noticeMessage: workspaceData.noticeMessage || '• 욕설이나 비방은 자제해 주세요.\n• 메시지 내용은 완료 후 수정이 불가능합니다.',
            qrMessage: workspaceData.qrMessage || '',
        };

        return settings;
    } catch (error) {
        console.error('Error fetching workspace settings:', error);
        throw error;
    }
};

// 메시지 삭제 함수
export const deleteMessage = async (workspaceId, messageId) => {
    const messageRef = doc(db, `workspaces/${workspaceId}/messages`, messageId);
    await deleteDoc(messageRef);
};

// 이미지 파일을 Firebase Storage에 업로드하는 함수
// export const uploadImage = async (file, folder) => {
//     if (!file) return null;
//     const storageRef = ref(storage, `${folder}/${file.name}`);
//
//     try {
//         const snapshot = await uploadBytes(storageRef, file);
//         const downloadURL = await getDownloadURL(snapshot.ref);
//         return downloadURL;
//     } catch (error) {
//         console.error('Error uploading image:', error);
//         throw error;
//     }
// };

export const subscribeToPendingMessages = (workspaceId, callback) => {
    try {
        const pendingMessagesRef = collection(db, 'workspaces', workspaceId, 'pending_messages');
        const q = query(pendingMessagesRef, orderBy('timestamp', 'desc'));

        return onSnapshot(q, (snapshot) => {
            const messages = snapshot.docs.map(doc => {
                const data = doc.data();
                console.log(data);
                // return {...data,id:doc.id}
                return {
                    id: doc.id,
                    ...data.message,
                    status: data.status,
                    timestamp: data.timestamp
                };
            });
            callback(messages);
        });
    } catch (error) {
        console.error('Error subscribing to pending messages:', error);
    }
};

export const getPendingMessages = async (workspaceId) => {
    const pendingMessagesRef = collection(db, 'workspaces', workspaceId, 'pending_messages');
    const q = query(pendingMessagesRef, orderBy('timestamp', 'desc'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            ...data.message,
            status: data.status,
            timestamp: data.timestamp
        };
    });
};

export const approveMessage = async (workspaceId, messageId) => {
    const pendingMessagesRef = collection(db, 'workspaces', workspaceId, 'pending_messages');
    const messagesRef = collection(db, 'workspaces', workspaceId, 'messages');

    // pending_messages에서 메시지 가져오기
    const messageDoc = await getDoc(doc(pendingMessagesRef, messageId));
    if (!messageDoc.exists()) {
        throw new Error('메시지를 찾을 수 없습니다.');
    }

    const messageData = messageDoc.data();

    // messages 컬렉션에 추가
    await addDoc(messagesRef, {
        affiliation: messageData.affiliation || "",
        image: messageData.image || null,
        message: messageData.message || "",
        name: messageData.name || "",
        phone: messageData.phone || "",
        position: messageData.position || "",
        timestamp: messageData.timestamp,
        status: 'approved'
    });

    // pending_messages에서 삭제
    await deleteDoc(doc(pendingMessagesRef, messageId));
};

export const rejectMessage = async (workspaceId, messageId) => {
    const pendingMessagesRef = collection(db, 'workspaces', workspaceId, 'pending_messages');

    // pending_messages에서 삭제
    await deleteDoc(doc(pendingMessagesRef, messageId));
};

// 메시지 내용 검사 함수 (비속어/혐오발언 필터)
export const checkMessageContent = async (message) => {
    try {
        let apiUrl = process.env.REACT_APP_MESSAGE_CHECK_API_URL || 'https://textfilter.widebrain.kr/v1/filter';
        
        // 개발 환경에서 CORS 문제를 해결하기 위해 프록시 사용
        if (process.env.NODE_ENV === 'development' && apiUrl.includes('textfilter.widebrain.kr')) {
            // 개발 환경: 로컬 프록시 사용
            const url = new URL(apiUrl);
            apiUrl = `/api/proxy${url.pathname}${url.search}`;
        }
        // 프로덕션 환경: 직접 API 호출 (API 서버에서 CORS 허용 필요)
        
        const requestBody = { text: message };
        
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API 호출 실패: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error checking message content:', error);
        throw error;
    }
};
